import '../styles/pdf-pages.css';
import '../styles/columnar-table.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateVenue } from '@/domain/impact-calculate-venue/entities/calculate-venue.entity';
import { CalculateVenueCo2 } from '@/domain/impact-calculate-venue/entities/calculate-venue-co2.entity';
import { CalculateVenueEnv } from '@/domain/impact-calculate-venue/entities/calculate-venue-env.entity';
import { CalculateVenueEss } from '@/domain/impact-calculate-venue/entities/calculate-venue-ess.entity';
import {
  selectImpactCalculateVenueCo2ForProject,
  selectImpactCalculateVenueEnvForProject,
  selectImpactCalculateVenueEssForProject,
  selectImpactCalculateVenueForProject,
} from '@/domain/impact-calculate-venue/impact-calculate-venue.selectors';
import { selectImpactEssAnswersMap } from '@/domain/impact-ess/impact-ess.selectors';
import { ProjectVenue } from '@/domain/project-venue/entities/project-venue.entity';
import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { GaugeScore } from '@/ui/components/score-bars/GaugeScore';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { getColorForScore } from '@/ui/utils/get-color-for-score';

import { formatProjectDatesTz } from '../../../utils/format-project-dates';
import CalendarMonthSvg from '../assets/calendar_month.svg';
import FairlyBackgroundLogo from '../assets/logo-fairly-clear-background.png';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { VenueDateManagement } from '../components/VenueDateManagement';
import { extendVenueEnvCalculate } from '../utils/compute-extend-env';
import { extendVenueEssCalculate } from '../utils/compute-extend-ess';
import {
  displayDecimal,
  formatTopicScore,
} from '../utils/display-scores.utils';
import {
  computeTeamParity,
  computeTotalTeamParity,
} from '../utils/team-parity.utils';

export function PdfVenuePage1({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const venue = useAppSelector(
    selectCurrentProjectVenue,
  ) satisfies ProjectVenue | null;
  const venueId = venue?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateVenueCo2ForProject(venueId),
  ) satisfies CalculateVenueCo2;

  const impactEnv = extendVenueEnvCalculate(
    useAppSelector(selectImpactCalculateVenueEnvForProject(venueId)),
  ) satisfies CalculateVenueEnv;

  const impactEss = extendVenueEssCalculate(
    useAppSelector(selectImpactCalculateVenueEssForProject(venueId)),
  ) satisfies CalculateVenueEss;

  const impactCalculateVenue = useAppSelector(
    selectImpactCalculateVenueForProject(venueId),
  ) satisfies CalculateVenue | null;

  const ESS_ANSWERS = useAppSelector(selectImpactEssAnswersMap);
  const ESS_3 = ESS_ANSWERS?.get('ESS_3')?.value || '';

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!venue) {
    return <h1>{translate('PdfExports.venue-not-found')}</h1>;
  }

  const impactEnvScore100 =
    !impactEnv.global.score || !impactEnv.global.maxScore
      ? 0
      : Math.round((impactEnv.global.score * 100) / impactEnv.global.maxScore);

  const impactEssScore100 =
    !impactEss.global.score || !impactEss.global.maxScore
      ? 0
      : Math.round((impactEss.global.score * 100) / impactEss.global.maxScore);

  const totalArtisticTeamParity = computeTeamParity(impactCo2.dates.byMonth);
  const totalTeamParity = computeTotalTeamParity(
    ESS_3,
    totalArtisticTeamParity,
  );

  /**
   * The title depends on the venue name, it is critical to display it
   * on one line, while keeping it readable.
   */
  const computedFontSize =
    venue.name.length <= 22
      ? 40
      : Math.max(20, 40 - (venue.name.length - 22) * (20 / 28));

  return (
    <div id="page1" className="pdf-page">
      <div className="content">
        <div className="pdf-header">
          <img
            className="venue-logo"
            src={venue.image || FairlyBackgroundLogo}
            alt={translate('PdfExports.venue-logo')}
          />
          <div>
            <h1 style={{ fontSize: computedFontSize }}>{venue.name}</h1>
            {venue.startDate && venue.endDate && (
              <div className="header-subtitles">
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="venue-date">
                  {formatProjectDatesTz(
                    venue.startDate || '',
                    venue.endDate || '',
                  )}
                </span>
              </div>
            )}
            <div className="pills">
              <span className="tag-pill pill-list">
                {translate('PdfExports.venue')}
              </span>
              <span className="tag-pill pill-list">
                {translate('PdfExports.ongoing')}
              </span>
            </div>
          </div>
        </div>

        <div className="pdf-separator"></div>

        <div className="columnar-table">
          <h2>{translate('PdfExports.columnar-table')}</h2>
          <div className="columnar-table-scores">
            <div className="score-item" id="overview-co2">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="co2"
                    scoreLetter={impactCo2.global.scoreLetter}
                  />
                  {translate('Projects.co2')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    score={100}
                    textColor="black"
                    bgColor="#DDDDDD99"
                    percentageScale={100}
                    overrideColor={
                      getColorForScore(impactCo2.global.scoreLetter).main
                    }
                    overrideLabel={' '}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      top: 50,
                      left: 0,
                      fontSize: '1.5rem',
                      textAlign: 'center',
                      width: '100%',
                      lineHeight: '1.5rem',
                    }}
                  >
                    <strong>
                      {impactCo2.global.kgeCO2ByParticipant.toFixed(1)}
                    </strong>
                    <br />
                    {translate('PdfExports.co2-per-participant')}
                  </span>
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-emissions')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactCo2.global.score)}
                      </span>{' '}
                      {translate('Projects.teCO2')}
                    </span>
                  </li>
                  <li className="score-highlight-secondary">
                    <span>
                      {translate('Projects.emissions-by-participant')}
                    </span>
                    <span>
                      {displayDecimal(impactCo2.global.kgeCO2ByParticipant)}{' '}
                      {translate('Projects.kgeCO2')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-env">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="env"
                    scoreLetter={impactEnv.global.scoreLetter}
                  />
                  {translate('Projects.env')}
                </h3>
              </div>
              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    textColor="black"
                    score={impactEnvScore100}
                    maxScore={100}
                    bgColor="#DDDDDD99"
                    overrideColor={
                      getColorForScore(impactEnv.global.scoreLetter).main
                    }
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEnv.global.score)}
                      </span>
                      /{impactEnv.global.maxScore}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.FOOD_BEVERAGES')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.structure.topics.FOOD_BEVERAGES,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.RESOURCES_POLLUTION')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.structure.topics.RESOURCES_POLLUTION,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.COMMUNICATION')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.structure.topics.COMMUNICATION,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Env.Topics.PUBLIC_ENGAGEMENT')}</span>
                    <span>
                      {formatTopicScore(
                        impactEnv.structure.topics.PUBLIC_ENGAGEMENT,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="score-item" id="overview-ess">
              <div className="score-item-header">
                <h3>
                  <PdfScoreCard
                    type="ess"
                    scoreLetter={impactEss.global.scoreLetter}
                  />
                  {translate('Projects.ess')}
                </h3>
              </div>

              <div className="score-item-value">
                <div
                  style={{
                    position: 'relative',
                    top: 10,
                    marginTop: 20,
                    marginBottom: 20,
                  }}
                >
                  <GaugeScore
                    score={impactEssScore100}
                    maxScore={100}
                    textColor="black"
                    bgColor="#DDDDDD99"
                    overrideColor={
                      getColorForScore(impactEss.global.scoreLetter).main
                    }
                  />
                </div>
                <ul>
                  <li className="score-highlight">
                    <span>{translate('PdfExports.total-score')}</span>
                    <span>
                      <span className="highlight">
                        {displayDecimal(impactEss.global.score)}
                      </span>
                      /{impactEss.global.maxScore}
                    </span>
                  </li>
                  <li>
                    <span>{translate('Ess.Topics.PARITY_EQUALITY')}</span>
                    <span>
                      {formatTopicScore(
                        impactEss.structure.topics.PARITY_EQUALITY,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.ACCESSIBILITY_SOCIAL_DIALOGUE')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.structure.topics
                          .ACCESSIBILITY_SOCIAL_DIALOGUE,
                      )}
                    </span>
                  </li>
                  <li>
                    <span>
                      {translate('Ess.Topics.LOCAL_IMPACT_RSE_ENGAGEMENT')}
                    </span>
                    <span>
                      {formatTopicScore(
                        impactEss.structure.topics.LOCAL_IMPACT_RSE_ENGAGEMENT,
                      )}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <VenueDateManagement
        data={{
          participants: impactCalculateVenue?.count?.participants ?? 0,
          dates: impactCalculateVenue?.count?.dates ?? 0,
          parity: totalTeamParity,
          totalStructureEmissions:
            impactCalculateVenue?.co2?.structure?.score ?? 0,
          totalDatesEmissions: impactCalculateVenue?.co2?.dates?.score ?? 0,
        }}
      />

      <PdfProjectPager current={1} total={totalPages} />
    </div>
  );
}
